import { DataButton } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { Icon } from "../../../components";
import { Tag } from "../../../components/tag/Tag";
import { StockOverviewContainerType } from "@sellar-io/api-v2/types";
import { capitalize, getProductDisplayNameByKey } from "@sellar-io/common";
import { SlideTransition } from "../../../components/transition/SlideTransition";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import {
  StockOverviewSortType,
  StockOverviewSortValues,
} from "../StockOverviewFilterTypes";
import { StockOverviewFilter } from "./StockOverviewFilter";
import { FilterPopover } from "../../../components/filterPopover/FilterPopover";
import { useMediaQuery } from "usehooks-ts";
import { useState } from "react";
import { ItemStatusType, ProductStatusType } from "../StockOverviewStore";

const getContainerIconName = (
  containerType: string | null,
): "CAN" | "BOTTLE" | "CASK" | "KEG" | null => {
  if (containerType === "can") {
    return "CAN";
  }
  if (containerType === "bottle") {
    return "BOTTLE";
  }
  if (containerType === "cask") {
    return "CASK";
  }
  if (containerType === "keg") {
    return "KEG";
  }

  return null;
};

const getItemStatusIcon = (
  itemStatus: string | null,
): "EYE" | "EYE_OFF" | null => {
  if (itemStatus === "visible") {
    return "EYE";
  }
  if (itemStatus === "hidden") {
    return "EYE_OFF";
  }

  return null;
};

const getFormattedContainerType = (containerType: string | null) => {
  if (containerType === "badge") {
    return "Badge";
  }

  if (containerType) {
    return getProductDisplayNameByKey(containerType);
  }

  return null;
};

const ActiveStockOverviewFilters = ({
  productStatus,
  itemStatus,
  containerType,
  isPreOrder,
  inStock,
  onClick,
}: {
  productStatus: ProductStatusType;
  itemStatus: ItemStatusType;
  containerType: StockOverviewContainerType | null;
  isPreOrder: boolean;
  inStock: boolean;
  onClick: (
    filterType:
      | "containerType"
      | "isPreOrder"
      | "inStock"
      | "productStatus"
      | "itemStatus",
  ) => void;
}) => {
  const anyFilterActive =
    !!containerType || isPreOrder || inStock || productStatus || itemStatus;

  const formattedContainerType =
    getFormattedContainerType(containerType) ?? "-";

  const containerIcon = getContainerIconName(containerType);
  const formattedProductStatus = capitalize(productStatus ?? "");
  const formattedItemStatus = capitalize(itemStatus ?? "");
  const itemStatusIcon = getItemStatusIcon(itemStatus);

  if (anyFilterActive) {
    return (
      <div className="flex w-full flex-wrap items-center gap-2 pt-2">
        {inStock && (
          <Tag
            className="flex items-center gap-1"
            onClick={() => onClick("inStock")}
          >
            <Icon icon={"CART"} />
            In Stock
          </Tag>
        )}
        {isPreOrder && (
          <Tag
            className="flex items-center gap-1"
            onClick={() => onClick("isPreOrder")}
          >
            <Icon icon={"PROGRESS_CLOCK"} />
            Pre-order
          </Tag>
        )}
        {containerType && (
          <Tag
            className="flex items-center gap-1"
            onClick={() => onClick("containerType")}
          >
            {!!containerIcon && <Icon icon={containerIcon} />}
            {formattedContainerType}
          </Tag>
        )}
        {productStatus && (
          <Tag
            className="flex items-center gap-1"
            onClick={() => onClick("productStatus")}
          >
            <Icon
              icon={"CIRCLE"}
              color={productStatus === "active" ? "green" : "red"}
              size={0.7}
            />
            {formattedProductStatus}
          </Tag>
        )}
        {itemStatus && (
          <Tag
            className="flex items-center gap-1"
            onClick={() => onClick("itemStatus")}
          >
            {!!itemStatusIcon && <Icon icon={itemStatusIcon} />}
            {formattedItemStatus}
          </Tag>
        )}
      </div>
    );
  }

  return null;
};

type FilterOptions = {
  containerType: StockOverviewContainerType | null;
  inStock: boolean;
  preOrder: boolean;
  productStatus: ProductStatusType;
  itemStatus: ItemStatusType;
};

export const SearchAndFilterCard = ({
  search,
  onSearchChange,
  filterOptions,
  setFilterOptions,
  columnSort,
  onColumnSortChange,
  totalResultCount,
}: {
  filterOptions: FilterOptions;
  setFilterOptions: (filterOptions: FilterOptions) => void;
  search: string;
  onSearchChange: (value: string) => void;
  columnSort: StockOverviewSortType;
  onColumnSortChange: (value: StockOverviewSortType) => void;
  totalResultCount: number;
}) => {
  const [mobileFilter, setMobileFilter] = useState(false);
  const [desktopFilter, setDesktopFilter] = useState(false);

  const isDesktop = useMediaQuery("(min-width: 1200px)");
  const isMobile = !isDesktop;

  return (
    <div>
      {/* mobile/desktop active filter */}

      <div
        className={twMerge(
          "mx-4 my-4 flex justify-start p-2",
          isDesktop && "mx-0",
        )}
      >
        <div className="text-base font-semibold text-zinc-500">
          {totalResultCount} results
        </div>
      </div>

      <div
        className={twMerge(
          "w-full border-collapse border-b border-zinc-300 bg-zinc-50 px-4 py-3",
          isDesktop && "rounded-t-lg border border-b-0",
        )}
      >
        {/* mobile/desktop search */}

        <div className={twMerge("flex items-center")}>
          <InputContainer
            className={twMerge(
              "mr-2 w-[300px] py-[0.4rem]",
              isMobile && "grow",
            )}
          >
            <InputText
              required={false}
              placeholder={`Search by sku, name or kind...`}
              value={search}
              onChange={(e) => {
                onSearchChange(e.target.value);
              }}
              className="w-full"
            />
          </InputContainer>

          {isMobile && (
            <DataButton
              onClick={() => setMobileFilter(!mobileFilter)}
              border={true}
              active={mobileFilter}
            >
              <Icon icon="FILTER_VARIANT" />
            </DataButton>
          )}
          {isDesktop && (
            <FilterPopover
              icon="FILTER_VARIANT"
              buttonText="Filter"
              compact={isMobile}
              active={desktopFilter}
              toggleActive={setDesktopFilter}
            >
              <StockOverviewFilter onDismiss={() => setDesktopFilter(false)} />
            </FilterPopover>
          )}
        </div>

        {/* mobile/desktop selected filters list*/}

        <ActiveStockOverviewFilters
          containerType={filterOptions.containerType}
          itemStatus={filterOptions.itemStatus}
          productStatus={filterOptions.productStatus}
          inStock={filterOptions.inStock}
          isPreOrder={filterOptions.preOrder}
          onClick={(filterType) => {
            if (filterType === "containerType") {
              setFilterOptions({
                ...filterOptions,
                containerType: null,
              });
            }
            if (filterType === "inStock") {
              setFilterOptions({
                ...filterOptions,
                inStock: false,
              });
            }
            if (filterType === "isPreOrder") {
              setFilterOptions({
                ...filterOptions,
                preOrder: false,
              });
            }
            if (filterType === "productStatus") {
              setFilterOptions({
                ...filterOptions,
                productStatus: null,
              });
            }
            if (filterType === "itemStatus") {
              setFilterOptions({
                ...filterOptions,
                itemStatus: null,
              });
            }
          }}
        />
      </div>

      {/* mobile stock overview  */}

      {isMobile && (
        <SlideTransition
          show={mobileFilter}
          className={"w-full"}
          parentClass="w-full"
        >
          {/* sort by section, only shown on mobile */}
          <>
            <div
              className={twMerge(
                "w-full",
                isMobile ? "px-4 pt-2" : "border-b border-zinc-300 p-4",
              )}
            >
              <span className="font-semibold">Sort by</span>
            </div>
            <div className="w-full border-b border-zinc-300 p-4">
              <InputContainer>
                <InputSelect
                  value={columnSort}
                  onChange={(e) => {
                    onColumnSortChange(e.target.value as StockOverviewSortType);
                    setMobileFilter(false);
                  }}
                >
                  {Object.values(StockOverviewSortValues).map((value) => (
                    <InputOption key={value.key} value={value.key}>
                      {value.name}
                    </InputOption>
                  ))}
                </InputSelect>
              </InputContainer>
            </div>
            <StockOverviewFilter
              onDismiss={() => setMobileFilter(false)}
              isMobile={isMobile}
            />
          </>
        </SlideTransition>
      )}
    </div>
  );
};
